import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, LogOut, X, FileText, Phone, MessageCircle, RefreshCw, Inbox } from "lucide-react";
import { API, IMG } from "../lib/site";
import { LogoMark } from "../components/Logo";

const GoogleG = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45a5.52 5.52 0 0 1-2.39 3.62v3h3.87c2.26-2.09 3.57-5.16 3.57-8.81z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.91l-3.87-3c-1.07.72-2.44 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.29v3.1A12 12 0 0 0 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28v-3.1H1.29a12 12 0 0 0 0 10.76l3.98-3.1z" />
    <path fill="#EA4335" d="M12 4.76c1.76 0 3.34.61 4.58 1.8l3.44-3.44A11.98 11.98 0 0 0 12 0 12 12 0 0 0 1.29 6.62l3.98 3.1C6.22 6.87 8.87 4.76 12 4.76z" />
  </svg>
);

const inputCls =
  "h-11 w-full border border-navy-900/15 bg-white px-3.5 text-sm text-navy-900 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/25";

const EMPTY = {
  title: "", developer: "", category: "prelaunch", location: "", tag: "", image: IMG.tower1,
  highlights: "", config: "", price_text: "", price_value: "", bhk: "", baths: "", area: "",
  furnishing: "", rent: "", maintenance: "", featured: false, brochure_path: "", brochure_name: "",
};

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const waNumber = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  return digits.length === 10 ? `91${digits}` : digits;
};

export default function Admin() {
  const location = useLocation();
  const [token, setToken] = useState(localStorage.getItem("ep_admin_token") || "");
  const [authed, setAuthed] = useState(location.state?.user ? true : null);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [listings, setListings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("listings");
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [brochureFile, setBrochureFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const load = () => axios.get(`${API}/listings`).then((r) => setListings(r.data)).catch(() => {});

  const loadLeads = () => {
    setLeadsLoading(true);
    axios
      .get(`${API}/leads`, authHeaders)
      .then((r) => setLeads(r.data))
      .catch(() => toast.error("Could not load leads."))
      .finally(() => setLeadsLoading(false));
  };

  useEffect(() => {
    if (authed !== null) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  useEffect(() => {
    if (authed) {
      load();
      loadLeads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      localStorage.setItem("ep_admin_token", data.token);
      setToken(data.token);
      setAuthed(true);
      toast.success("Welcome back, Admin.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check credentials.");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
    localStorage.removeItem("ep_admin_token");
    setToken("");
    setAuthed(false);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      highlights: editing.highlights.split(",").map((h) => h.trim()).filter(Boolean),
      price_value: numOrNull(editing.price_value),
      bhk: numOrNull(editing.bhk),
      baths: numOrNull(editing.baths),
      area: numOrNull(editing.area),
      rent: numOrNull(editing.rent),
    };
    delete payload.id;
    delete payload.created_at;
    delete payload.updated_at;
    try {
      if (editing.id) {
        await axios.put(`${API}/listings/${editing.id}`, payload, authHeaders);
        toast.success("Listing updated.");
      } else {
        await axios.post(`${API}/listings`, payload, authHeaders);
        toast.success("Listing added — open it again to attach a brochure PDF.");
      }
      setEditing(null);
      setBrochureFile(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) { logout(); toast.error("Session expired — please log in again."); }
      else toast.error("Could not save listing.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this listing permanently?")) return;
    try {
      await axios.delete(`${API}/listings/${id}`, authHeaders);
      toast.success("Listing deleted.");
      load();
    } catch {
      toast.error("Could not delete listing.");
    }
  };

  const uploadBrochure = async () => {
    if (!brochureFile || !editing?.id) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", brochureFile);
    try {
      const { data } = await axios.post(`${API}/listings/${editing.id}/brochure`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setEditing((f) => ({ ...f, brochure_path: data.brochure_path, brochure_name: data.brochure_name }));
      setBrochureFile(null);
      toast.success("Brochure uploaded — visitors now download it after registering.");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed — PDF up to 15 MB only.");
    } finally {
      setUploading(false);
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, authHeaders);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Could not delete enquiry.");
    }
  };

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-950" data-testid="admin-auth-loading">
        <Loader2 className="h-7 w-7 animate-spin text-gold-400" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-950 px-5 pt-20" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-white p-8 shadow-[0_40px_90px_rgb(8,16,39,0.6)]" data-testid="admin-login-form">
          <div className="mb-7 flex flex-col items-center text-center">
            <LogoMark className="h-14 w-auto" />
            <h1 className="mt-4 font-serif text-2xl font-medium text-navy-900">Admin Console</h1>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-gray-400">Estate Paradise — Listings Manager</p>
          </div>
          {error && <p className="mb-4 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-login-error">{error}</p>}
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-navy-900/60">Email / Username</label>
          <input data-testid="admin-email-input" required value={creds.email} onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))} className={inputCls} placeholder="estateparadise@admin" />
          <label className="mb-1.5 mt-4 block text-[11px] font-bold uppercase tracking-[0.18em] text-navy-900/60">Password</label>
          <input data-testid="admin-password-input" type="password" required value={creds.password} onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))} className={inputCls} placeholder="••••••••••" />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="mt-6 flex w-full items-center justify-center gap-2 bg-gold-500 px-6 py-3.5 text-[13px] font-bold uppercase tracking-[0.14em] text-navy-900 transition-colors duration-300 hover:bg-gold-400 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign In
          </button>
          <div className="my-5 flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-navy-900/10" />
            <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-gray-400">or</span>
            <span className="h-px flex-1 bg-navy-900/10" />
          </div>
          <button
            type="button"
            onClick={googleLogin}
            data-testid="admin-google-login-btn"
            className="flex w-full items-center justify-center gap-3 border border-navy-900/20 px-6 py-3.5 text-[13px] font-bold uppercase tracking-[0.14em] text-navy-900 transition-[border-color,background-color] duration-300 hover:border-navy-900 hover:bg-navy-900 hover:text-white"
          >
            <GoogleG />
            Continue with Google
          </button>
          <p className="mt-3 text-center text-[11px] text-gray-400">
            Restricted to the authorized admin Google account.
          </p>
        </form>
      </div>
    );
  }

  const Field = ({ label, k, type = "text", placeholder = "" }) => (
    <div>
      <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.16em] text-navy-900/60">{label}</label>
      <input
        type={type}
        value={editing[k]}
        placeholder={placeholder}
        onChange={(e) => setEditing((f) => ({ ...f, [k]: type === "checkbox" ? e.target.checked : e.target.value }))}
        className={inputCls}
        data-testid={`listing-field-${k}`}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-cream pb-24 pt-28" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-gold-600">Estate Paradise</p>
            <h1 className="mt-1 font-serif text-3xl font-light text-navy-900">Admin Console</h1>
          </div>
          <div className="flex gap-3">
            {tab === "listings" && (
              <button
                data-testid="add-listing-btn"
                onClick={() => setEditing({ ...EMPTY })}
                className="flex items-center gap-2 bg-gold-500 px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-navy-900 transition-colors duration-300 hover:bg-gold-400"
              >
                <Plus className="h-4 w-4" /> Add Listing
              </button>
            )}
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 border border-navy-900/20 px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-navy-900 transition-colors duration-300 hover:border-navy-900"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-2" data-testid="admin-tabs">
          <button
            data-testid="tab-listings-btn"
            onClick={() => setTab("listings")}
            className={`flex items-center gap-2 border px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] transition-colors duration-300 ${
              tab === "listings" ? "border-navy-900 bg-navy-900 text-white" : "border-navy-900/20 bg-white text-navy-900/60 hover:border-gold-500"
            }`}
          >
            <FileText className="h-4 w-4" /> Listings ({listings.length})
          </button>
          <button
            data-testid="tab-leads-btn"
            onClick={() => { setTab("leads"); loadLeads(); }}
            className={`flex items-center gap-2 border px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] transition-colors duration-300 ${
              tab === "leads" ? "border-navy-900 bg-navy-900 text-white" : "border-navy-900/20 bg-white text-navy-900/60 hover:border-gold-500"
            }`}
          >
            <Inbox className="h-4 w-4" /> Leads Inbox ({leads.length})
          </button>
        </div>

        {tab === "listings" && (
          <div className="mt-6 space-y-3" data-testid="admin-listings-table">
            {listings.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-4 border border-navy-900/10 bg-white p-4" data-testid={`admin-listing-row-${l.id}`}>
                <img src={l.image} alt="" className="h-14 w-20 shrink-0 object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-lg font-medium text-navy-900">
                    {l.title}
                    {l.brochure_name && (
                      <span className="ml-2 inline-flex items-center gap-1 border border-gold-500/50 bg-gold-50 px-2 py-0.5 align-middle text-[9px] font-bold uppercase tracking-[0.14em] text-gold-700">
                        <FileText className="h-3 w-3" /> PDF
                      </span>
                    )}
                  </p>
                  <p className="text-xs uppercase tracking-[0.14em] text-gray-400">
                    {l.category} &middot; {l.location} {l.price_text ? `· ${l.price_text}` : ""} {l.rent ? `· ₹${l.rent.toLocaleString("en-IN")}/mo` : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid={`edit-listing-${l.id}`}
                    onClick={() =>
                      setEditing({
                        ...EMPTY, ...l,
                        highlights: (l.highlights || []).join(", "),
                        price_value: l.price_value ?? "", bhk: l.bhk ?? "", baths: l.baths ?? "",
                        area: l.area ?? "", rent: l.rent ?? "",
                      })
                    }
                    className="flex h-10 w-10 items-center justify-center border border-navy-900/20 text-navy-900 transition-colors duration-300 hover:border-gold-500 hover:text-gold-600"
                    aria-label="Edit listing"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`delete-listing-${l.id}`}
                    onClick={() => remove(l.id)}
                    className="flex h-10 w-10 items-center justify-center border border-red-200 text-red-600 transition-colors duration-300 hover:bg-red-50"
                    aria-label="Delete listing"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
            {listings.length === 0 && (
              <p className="border border-dashed border-navy-900/20 bg-white p-10 text-center text-sm text-gray-500" data-testid="admin-empty">
                No listings yet — add your first property.
              </p>
            )}
          </div>
        )}

        {tab === "leads" && (
          <div className="mt-6" data-testid="admin-leads-tab">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {leads.length} enquir{leads.length === 1 ? "y" : "ies"} — newest first. Each one is also emailed to you.
              </p>
              <button
                data-testid="leads-refresh-btn"
                onClick={loadLeads}
                className="flex items-center gap-2 border border-navy-900/20 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-navy-900 transition-colors duration-300 hover:border-navy-900"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${leadsLoading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
            <div className="space-y-3" data-testid="admin-leads-list">
              {leads.map((lead) => (
                <div key={lead.id} className="border border-navy-900/10 bg-white p-5" data-testid={`lead-row-${lead.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-serif text-lg font-medium text-navy-900">{lead.name}</p>
                      <p className="mt-0.5 text-sm text-gray-500">
                        {lead.phone} &middot; {lead.email}
                      </p>
                      <div className="mt-2.5 flex flex-wrap gap-2">
                        {lead.interest && <span className="bg-navy-900 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white">{lead.interest}</span>}
                        {lead.budget && <span className="border border-gold-500/50 bg-gold-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-gold-700">{lead.budget}</span>}
                        {lead.project && <span className="border border-navy-900/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-navy-900/70">{lead.project}</span>}
                      </div>
                      {lead.message && <p className="mt-2.5 text-sm italic leading-relaxed text-gray-500">"{lead.message}"</p>}
                      <p className="mt-2.5 text-[11px] uppercase tracking-[0.14em] text-gray-400">
                        {lead.source} &middot; {lead.created_at ? new Date(lead.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <a
                        href={`tel:+${waNumber(lead.phone)}`}
                        data-testid={`lead-call-${lead.id}`}
                        className="flex h-10 w-10 items-center justify-center border border-navy-900/20 text-navy-900 transition-colors duration-300 hover:border-navy-900"
                        aria-label="Call lead"
                      >
                        <Phone className="h-4 w-4" />
                      </a>
                      <a
                        href={`https://wa.me/${waNumber(lead.phone)}?text=${encodeURIComponent(`Hi ${lead.name}, this is Estate Paradise following up on your enquiry${lead.project ? ` about ${lead.project}` : ""}.`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`lead-wa-${lead.id}`}
                        className="flex h-10 w-10 items-center justify-center border border-[#25D366]/40 text-[#25D366] transition-colors duration-300 hover:bg-[#25D366]/10"
                        aria-label="WhatsApp lead"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </a>
                      <button
                        data-testid={`lead-delete-${lead.id}`}
                        onClick={() => removeLead(lead.id)}
                        className="flex h-10 w-10 items-center justify-center border border-red-200 text-red-600 transition-colors duration-300 hover:bg-red-50"
                        aria-label="Delete lead"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {leads.length === 0 && !leadsLoading && (
                <p className="border border-dashed border-navy-900/20 bg-white p-10 text-center text-sm text-gray-500" data-testid="leads-empty">
                  No enquiries yet — they appear here the moment a form is submitted.
                </p>
              )}
              {leadsLoading && (
                <p className="flex items-center justify-center gap-2 p-10 text-sm text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading enquiries…
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-950/60 p-4 backdrop-blur-sm" onClick={() => setEditing(null)} data-testid="listing-editor-overlay">
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto bg-white p-7 shadow-[0_40px_90px_rgb(8,16,39,0.5)]"
            data-testid="listing-editor-form"
          >
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-serif text-2xl font-medium text-navy-900">{editing.id ? "Edit Listing" : "Add Listing"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="flex h-9 w-9 items-center justify-center border border-navy-900/20 text-navy-900" aria-label="Close editor" data-testid="listing-editor-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Title *" k="title" />
              <Field label="Developer" k="developer" />
              <div>
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.16em] text-navy-900/60">Category *</label>
                <select value={editing.category} onChange={(e) => setEditing((f) => ({ ...f, category: e.target.value }))} className={inputCls} data-testid="listing-field-category">
                  <option value="prelaunch">Pre-Launch</option>
                  <option value="resale">Resale</option>
                  <option value="rental">Rental</option>
                </select>
              </div>
              <Field label="Location" k="location" />
              <Field label="Tag / Badge" k="tag" placeholder="Pre-Launch Hot Deal" />
              <Field label="Config" k="config" placeholder="2 & 3 BHK" />
              <div className="sm:col-span-2">
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.16em] text-navy-900/60">Image URL</label>
                <input value={editing.image} onChange={(e) => setEditing((f) => ({ ...f, image: e.target.value }))} className={inputCls} data-testid="listing-field-image" />
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(IMG).filter(([k]) => k !== "trust" && k !== "hero" && k !== "skyline").slice(0, 8).map(([k, url]) => (
                    <button key={k} type="button" onClick={() => setEditing((f) => ({ ...f, image: url }))} className={`border px-2 py-1 text-[10px] uppercase tracking-wider ${editing.image === url ? "border-gold-500 bg-gold-50 text-gold-700" : "border-navy-900/15 text-gray-500"}`}>
                      {k}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.16em] text-navy-900/60">Highlights (comma separated)</label>
                <input value={editing.highlights} onChange={(e) => setEditing((f) => ({ ...f, highlights: e.target.value }))} className={inputCls} placeholder="Park facing, High floor, 2 parking" data-testid="listing-field-highlights" />
              </div>
              <Field label="Price Text" k="price_text" placeholder="₹68 L or Price on Request" />
              <Field label="Price Value (Lakh, for filters)" k="price_value" type="number" placeholder="68" />
              <Field label="BHK" k="bhk" type="number" />
              <Field label="Baths" k="baths" type="number" />
              <Field label="Carpet Area (sq.ft)" k="area" type="number" />
              <Field label="Rent (₹/month)" k="rent" type="number" />
              <Field label="Furnishing" k="furnishing" placeholder="Furnished / Semi-Furnished" />
              <Field label="Maintenance" k="maintenance" placeholder="Included in rent" />
              <label className="flex items-center gap-3 text-sm text-navy-900">
                <input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing((f) => ({ ...f, featured: e.target.checked }))} className="h-4 w-4 accent-[#C5A059]" data-testid="listing-field-featured" />
                Feature this listing at the top
              </label>
              <div className="border border-dashed border-navy-900/20 bg-cream p-4 sm:col-span-2" data-testid="brochure-upload-section">
                <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.16em] text-navy-900/60">Brochure PDF</label>
                {editing.id ? (
                  <>
                    {editing.brochure_name ? (
                      <p className="flex items-center gap-2 text-sm text-navy-900" data-testid="brochure-current">
                        <FileText className="h-4 w-4 text-gold-600" />
                        {editing.brochure_name}
                        <a href={`${API}/brochures/${editing.id}`} target="_blank" rel="noopener noreferrer" className="text-xs font-bold uppercase tracking-wider text-gold-600 underline" data-testid="brochure-preview-link">
                          preview
                        </a>
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400">No brochure yet — visitors get a WhatsApp message instead of a download.</p>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={(e) => setBrochureFile(e.target.files?.[0] || null)}
                        className="text-xs text-gray-500 file:mr-3 file:border file:border-navy-900/20 file:bg-white file:px-3 file:py-2 file:text-[11px] file:font-bold file:uppercase file:tracking-wider file:text-navy-900"
                        data-testid="brochure-file-input"
                      />
                      <button
                        type="button"
                        onClick={uploadBrochure}
                        disabled={!brochureFile || uploading}
                        data-testid="brochure-upload-btn"
                        className="flex items-center gap-2 bg-navy-900 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-navy-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {uploading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Upload PDF
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-gray-400">Save the listing first — then reopen it to attach a brochure PDF.</p>
                )}
              </div>
            </div>
            <button type="submit" data-testid="listing-save-btn" className="mt-7 w-full bg-gold-500 px-6 py-3.5 text-[13px] font-bold uppercase tracking-[0.14em] text-navy-900 transition-colors duration-300 hover:bg-gold-400">
              Save Listing
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
